import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useLocation, useSearch } from "wouter";
import { useEffect, useState } from "react";
import { Search, Wallet, Blocks, File, Calendar, LoaderCircle, Bitcoin, X } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { format, subDays, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { cn } from "@/lib/utils";
import { DayPicker, MonthsDropdown, NavProps } from "react-day-picker"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { apiRequest } from "@/lib/queryClient";
import { useQuery } from "@tanstack/react-query";
import { fetchUserUsageStats } from "@/data-access/users";
import { useAuth } from "@/hooks/use-auth";
import { useUsageLimitStore } from "@/stores/usageLimitStore";
import { verifySearch } from "@/data-access/blockchain";

// Helper function to get date range from range parameter
function getDateRangeFromRange(range: string | null): { startDate: Date | null; endDate: Date | null } {
  const today = new Date();
  
  switch (range) {
    case 'last_week':
      return {
        startDate: subDays(today, 7),
        endDate: today
      };
    case 'last_month':
      return {
        startDate: startOfMonth(subMonths(today, 1)),
        endDate: endOfMonth(subMonths(today, 1))
      };
    case 'last_3_months':
      return {
        startDate: startOfMonth(subMonths(today, 3)),
        endDate: endOfMonth(subMonths(today, 1))
      };
    default:
      return { startDate: null, endDate: null };
  }
}

// Simple schema for the search form - validation will be done on the backend
const searchSchema = z.object({
  query: z.string().min(1, "Search value is required"),
  startDate: z.date().nullable(),
  endDate: z.date().nullable(),
});

type SearchValues = z.infer<typeof searchSchema>;

interface BlockSeerSearchProps {}

export default function BlockSeerSearch({}: BlockSeerSearchProps) {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const searchParams = new URLSearchParams(search);
  
  // Get parameters from URL
  const query = searchParams.get("query") || "";
  const rangeParam = searchParams.get("range") || null;
  const startDateParam = searchParams.get("startDate") || null;
  const endDateParam = searchParams.get("endDate") || null;
  
  const [detectedType, setDetectedType] = useState<"address" | "transaction" | "block_hash" | "block_height" | null>(null);
  const [isManualDateChange, setIsManualDateChange] = useState(false);
  const [startDateMonth, setStartDateMonth] = useState<Date | undefined>(undefined);
  const [endDateMonth, setEndDateMonth] = useState<Date | undefined>(undefined);

  // Determine initial selected date range based on URL parameters
  const getInitialDateRange = (): string => {
    // If we have a range parameter from URL, use that
    if (rangeParam) {
      switch (rangeParam) {
        case 'last_week':
          return 'last-week';
        case 'last_month':
          return 'last-month';
        case 'last_3_months':
          return 'last-3-months';
        default:
          return 'all-transactions';
      }
    }
    
    // If we have manual dates in URL, don't preselect any option
    if (startDateParam && endDateParam) {
      return '';
    }
    
    // Default to last-week
    return 'last-week';
  };

  const [selectedDateRange, setSelectedDateRange] = useState<string>(getInitialDateRange());

  // Get current user and usage stats
  const { user } = useAuth();
  const { data: usageStats } = useQuery({
    queryKey: ['/api/users/usage-stats'],
    queryFn: fetchUserUsageStats,
    enabled: !!user, // Only fetch if user is authenticated
  });
  
  const { showModal } = useUsageLimitStore();
  
  const form = useForm<SearchValues>({
    resolver: zodResolver(searchSchema),
    defaultValues: (() => {
      // Determine actual dates to use - prioritize manual dates over range
      let startDate, endDate;
      
      if (startDateParam && endDateParam) {
        // Manual dates provided - use those
        startDate = (startDateParam && !isNaN(Date.parse(startDateParam))) ? new Date(startDateParam) : null;
        endDate = (endDateParam && !isNaN(Date.parse(endDateParam))) ? new Date(endDateParam) : null;
      } else if (rangeParam) {
        // Range parameter provided - calculate dates
        const { startDate: rangeStart, endDate: rangeEnd } = getDateRangeFromRange(rangeParam);
        startDate = rangeStart;
        endDate = rangeEnd;
      } else {
        // No parameters - use default last week
        startDate = subDays(new Date(), 7);
        endDate = new Date();
      }
      
      return {
        query,
        startDate,
        endDate,
      };
    })(),
  });

  // Initialize month states based on form values
  useEffect(() => {
    const startDate = form.getValues("startDate");
    const endDate = form.getValues("endDate");
    
    if (startDate) {
      setStartDateMonth(startDate);
    }
    if (endDate) {
      setEndDateMonth(endDate);
    }
  }, []);

  // Update detected type when query changes - now just for UI hints
  // Actual validation will be done on the backend
  useEffect(() => {
    const currentQuery = form.watch("query");

    // Simple heuristics for UI display only (icon changes)
    let guessedType: typeof detectedType = null;
    if (currentQuery) {
      if (/^\d+$/.test(currentQuery)) {
        guessedType = "block_height";
      } else if (/^[a-fA-F0-9]{64}$/.test(currentQuery)) {
        guessedType = currentQuery.startsWith("0") ? "block_hash" : "transaction";
      } else if (/^(bc1|[13])[a-zA-HJ-NP-Z0-9]+$/.test(currentQuery)) {
        guessedType = "address";
      }
    }

    setDetectedType(guessedType);

    // Reset dates when switching away from address search
    if (guessedType !== "address") {
      form.setValue("startDate", null);
      form.setValue("endDate", null);
      setSelectedDateRange("last-week");
      setIsManualDateChange(false);
      setStartDateMonth(undefined);
      setEndDateMonth(undefined);
    } else {
      // Set default date range to Last Week when address is detected (only if no existing range)
      if (selectedDateRange === "last-week" && !isManualDateChange) {
        const today = new Date();
        const startWeek = subDays(today, 7);
        form.setValue("startDate", startWeek);
        form.setValue("endDate", today);
        setStartDateMonth(startWeek);
        setEndDateMonth(today);
      }
    }
  }, [form.watch("query")]);

  const handleSearch = async (data: SearchValues) => {
    // Check usage limit before proceeding
    if (usageStats && usageStats.usage.searches >= 300 && !usageStats.isAdmin) {
      showModal({
        usageType: 'searches',
        current: usageStats.usage.searches,
        limit: 300,
        resetDate: usageStats.resetDate,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Call backend to verify and determine search type
      let searchType: "address" | "transaction" | "block_hash" | "block_height";

      try {
        const verificationResult = await verifySearch(data.query.trim());
        searchType = verificationResult.type;
      } catch (error) {
        // Verification failed - show error to user
        form.setError("query", {
          type: "manual",
          message: error instanceof Error ? error.message : "The wallet address, block height, block hash, or transaction you entered is invalid or not found",
        });
        return; // Stop further processing if validation fails
      }

      const params = new URLSearchParams();
      if (data.query) {
        params.set("query", data.query.trim());
      }

      // Handle date parameters for address searches
      if (searchType === "address") {
        if (isManualDateChange) {
          // Manual date selection - use startDate and endDate parameters
          if (data.startDate instanceof Date && !isNaN(data.startDate.getTime())) {
            params.set("startDate", data.startDate.toISOString().split('T')[0]);
          }
          if (data.endDate instanceof Date && !isNaN(data.endDate.getTime())) {
            params.set("endDate", data.endDate.toISOString().split('T')[0]);
          }
          // Don't set range parameter when using manual dates
        } else if (selectedDateRange && selectedDateRange !== 'all-transactions') {
          // Predefined range selection - use range parameter
          const urlRange = selectedDateRange === 'last-week' ? 'last_week' :
                           selectedDateRange === 'last-month' ? 'last_month' :
                           selectedDateRange === 'last-3-months' ? 'last_3_months' :
                           null;
          if (urlRange) {
            params.set("range", urlRange);
          }
          // Don't set startDate and endDate when using range
        }
        // If selectedDateRange is 'all-transactions', don't set any date params
      }

      let path = "/search";
      switch (searchType) {
        case "address":
          path = "/search/address";
          break;
        case "transaction":
          path = "/search/transaction";
          break;
        case "block_hash":
          path = "/search/block-hash";
          break;
        case "block_height":
          path = "/search/block-height";
          break;
      }
      setLocation(params.toString() ? `${path}?${params.toString()}` : path);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const onSubmit = handleSearch;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getPlaceholder = () => {
    return "Enter a Bitcoin wallet address, transaction hash, block height, or block hash";
  };

  const getSearchTypeIcon = () => {
    switch (detectedType) {
      case "address":
        return <Wallet className="h-4 w-4 text-blue-500" />;
      case "transaction":
        return <File className="h-4 w-4 text-blue-500" />;
      case "block_hash":
      case "block_height":
        return <Blocks className="h-4 w-4 text-blue-500" />;
      default:
        return <Search className="h-4 w-4 text-blue-500" />;
    }
  };

  const isFormValid = () => {
    const values = form.getValues();

    // Must have a query
    if (!values.query || values.query.trim().length === 0) return false;

    // If it looks like an address (for UI purposes only), check date requirements
    const looksLikeAddress = /^(bc1|[13])[a-zA-HJ-NP-Z0-9]+$/.test(values.query);

    if (looksLikeAddress) {
      // For "all-transactions", we don't need dates
      if (selectedDateRange === "all-transactions") {
        return true;
      }
      // For other date range options, we need both dates
      return values.startDate !== null && values.endDate !== null;
    }

    return true;
  };

  return (
    <div className="max-w-4xl mx-auto py-0 relative">
      <Form {...form}>
        <form 
          onSubmit={form.handleSubmit(onSubmit)} 
          className="space-y-6 p-8 bg-card border border-border rounded-xl shadow-lg"
        >
          {/* Title and Icon */}
          <div className="flex flex-col mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-warning">
                <img src="/assets/btc_logo.png" alt="DMG Blockchain" className="w-10 h-10 rounded-lg" />
              </span>
              <div className="flex flex-col">
                <h3 className="text-foreground text-2xl font-semibold">Explore</h3>
                <p className="text-muted-foreground text-sm">Search and analyze Bitcoin addresses, transactions, and blocks</p>
              </div>
            </div>
          </div>

          {/* Search Query Input */}
          <FormField
            control={form.control}
            name="query"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="relative">
                    <Input 
                      placeholder={getPlaceholder()} 
                      className="focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:transition-none bg-background border border-input h-12 rounded-lg text-foreground placeholder:text-muted-foreground w-full pl-10 pr-10 transition-all duration-200"
                      {...field} 
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        form.trigger("query");
                      }}
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2">
                      {getSearchTypeIcon()}
                    </div>
                    {field.value && (
                      <button
                        type="button"
                        onClick={() => {
                          field.onChange("");
                          form.trigger("query");
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </FormControl>
                <FormMessage className="text-orange-500 text-xs" />
              </FormItem>
            )}
          />

          {/* Date Range Filter Section - Only shown for address search */}
          {detectedType === "address" && (
            <div className="flex flex-col gap-4">
              {/* Quick Date Range Selection */}
              <div className="mt-2">
                <RadioGroup
                  defaultValue="last-week"
                  value={selectedDateRange}
                  onValueChange={(value) => {
                    setSelectedDateRange(value);
                    setIsManualDateChange(false); // Clear manual date flag when using preset
                    const today = new Date();
                    switch (value) {
                      case "last-week":
                        const startWeek = subDays(today, 7);
                        form.setValue("startDate", startWeek);
                        form.setValue("endDate", today);
                        setStartDateMonth(startWeek);
                        setEndDateMonth(today);
                        break;
                      case "last-month":
                        const startMonth = startOfMonth(subMonths(today, 1));
                        const endMonth = endOfMonth(subMonths(today, 1));
                        form.setValue("startDate", startMonth);
                        form.setValue("endDate", endMonth);
                        setStartDateMonth(startMonth);
                        setEndDateMonth(endMonth);
                        break;
                      case "last-3-months":
                        const start3Months = startOfMonth(subMonths(today, 3));
                        const end3Months = endOfMonth(subMonths(today, 1));
                        form.setValue("startDate", start3Months);
                        form.setValue("endDate", end3Months);
                        setStartDateMonth(start3Months);
                        setEndDateMonth(end3Months);
                        break;
                      case "all-transactions":
                        form.setValue("startDate", null);
                        form.setValue("endDate", null);
                        setStartDateMonth(undefined);
                        setEndDateMonth(undefined);
                        break;
                    }
                  }}
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="last-week" id="last-week" />
                    <label htmlFor="last-week" className="text-sm text-foreground cursor-pointer">
                      Last week
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="last-month" id="last-month" />
                    <label htmlFor="last-month" className="text-sm text-foreground cursor-pointer">
                      Last month
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="last-3-months" id="last-3-months" />
                    <label htmlFor="last-3-months" className="text-sm text-foreground cursor-pointer">
                      Last 3 months
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="all-transactions" id="all-transactions" />
                    <label htmlFor="all-transactions" className="text-sm text-foreground cursor-pointer">
                      All Transactions
                    </label>
                  </div>
                </RadioGroup>
              </div>

              {/* Date Range Inputs - Hidden when "All Transactions" is selected */}
              {selectedDateRange !== "all-transactions" && (
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-end justify-between">
                          <FormLabel className="text-sm font-medium text-foreground">Start Date *</FormLabel>
                        </div>
                        <FormControl>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="relative w-full bg-background border border-input h-12 pr-12 rounded-lg text-left focus-visible:ring-primary focus-visible:ring-1 focus-visible:border-primary text-foreground flex items-center transition-all duration-200"
                              >
                                <span className={cn('pl-3', {
                                  "text-muted-foreground": !field.value,
                                })}>
                                  {field.value ? format(field.value, "yyyy/MM/dd") : "Pick a date"}
                                </span>
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-primary pointer-events-none">
                                  <Calendar className="h-4 w-4" />
                                </span>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="p-2 w-auto bg-popover border border-border rounded-lg shadow-md">
                              <DayPicker
                                mode="single"
                                captionLayout="dropdown"
                                selected={field.value ?? undefined}
                                month={startDateMonth ?? field.value ?? undefined}
                                onMonthChange={setStartDateMonth}
                                onSelect={(date) => {
                                  field.onChange(date ?? null);
                                  form.trigger("startDate");
                                  setIsManualDateChange(true);
                                  setSelectedDateRange(''); // Clear radio selection when manually changing dates
                                  if (date) {
                                    setStartDateMonth(date);
                                  }
                                }}
                              />
                            </PopoverContent>
                          </Popover>
                        </FormControl>
                        <FormMessage className="text-red-500 text-xs" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-end justify-between">
                          <FormLabel className="text-sm font-medium text-foreground">End Date *</FormLabel>
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            className="h-5 p-2"
                            onClick={() => {
                              const today = new Date();
                              field.onChange(today);
                              form.trigger("endDate");
                              setIsManualDateChange(true);
                              setSelectedDateRange(''); // Clear radio selection when manually changing dates
                              setEndDateMonth(today); // Update calendar to show current month
                            }}
                          >
                            Today
                          </Button>
                        </div>
                        <FormControl>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="relative w-full bg-background border border-input h-12 pr-12 rounded-lg text-left focus-visible:ring-primary focus-visible:ring-1 focus-visible:border-primary text-foreground flex items-center transition-all duration-200"
                              >
                                <span className={cn('pl-3', {
                                  "text-muted-foreground": !field.value,
                                })}>
                                  {field.value ? format(field.value, "yyyy/MM/dd") : "Pick a date"}
                                </span>
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-primary pointer-events-none">
                                  <Calendar className="h-4 w-4" />
                                </span>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="p-2 w-auto bg-popover border border-border rounded-lg shadow-md">
                              <DayPicker
                                mode="single"
                                captionLayout="dropdown"
                                selected={field.value ?? undefined}
                                month={endDateMonth ?? field.value ?? undefined}
                                onMonthChange={setEndDateMonth}
                                onSelect={(date) => {
                                  field.onChange(date ?? null);
                                  form.trigger("endDate");
                                  setIsManualDateChange(true);
                                  setSelectedDateRange(''); // Clear radio selection when manually changing dates
                                  if (date) {
                                    setEndDateMonth(date);
                                  }
                                }}
                              />
                            </PopoverContent>
                          </Popover>
                        </FormControl>
                        <FormMessage className="text-red-500 text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>
          )}

          {/* Search Button */}
          <div className="flex justify-center pt-4">
            <Button 
              type="submit" 
              disabled={!isFormValid()}
              className="bg-primary hover:bg-primary/90 text-primary-foreground py-3 px-8 h-auto rounded-lg text-sm font-medium shadow-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Search Blockseer
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
